'use client'

import { STEPPED_MODAL_STORAGE_KEY, ModalStep } from '../../../..'
import React, {
    createContext,
    Dispatch,
    ReactElement,
    ReactNode,
    SetStateAction,
    useEffect,
    useMemo,
    useState,
} from 'react'

interface SteppedModalContextType {
    isModalVisible: boolean
    setModalVisibility: Dispatch<SetStateAction<boolean>>
    showModal: () => void
    markModalAsSeen: () => void
    steps: ModalStep[]
}

export const SteppedModalContext = createContext<SteppedModalContextType>({
    isModalVisible: false,
    setModalVisibility: () => {},
    showModal: () => {},
    markModalAsSeen: () => {},
    steps: [],
})

interface Props {
    children: ReactNode
    steps: ModalStep[]
    showWhenUserHasNotSeenModal?: boolean
    localStorageKey?: string
}

/**
 * Reads whether the stepped modal has already been dismissed for the given storage key.
 * Returns true on the server (no localStorage) so server rendering never assumes "not seen".
 */
export const hasUserSeenSteppedModal = (localStorageKey: string = STEPPED_MODAL_STORAGE_KEY): boolean => {
    if (typeof window === 'undefined') return true

    return !!localStorage.getItem(localStorageKey)
}

/**
 * To use the SteppedModal, you need to wrap your app with this provider.
 * When adding this to a new project, remember to add the message keys in
 * the steppedModalConstants file to the dictionary.
 */
export const SteppedModalProvider = ({
    children,
    steps,
    showWhenUserHasNotSeenModal = true,
    localStorageKey = STEPPED_MODAL_STORAGE_KEY,
}: Props): ReactElement => {
    const [isModalVisible, setModalVisibility] = useState(false)

    const showModal = (): void => {
        setModalVisibility(true)
    }

    const markModalAsSeen = (): void => {
        if (typeof window === 'undefined') return

        localStorage.setItem(localStorageKey, 'true')
    }

    useEffect(() => {
        if (typeof window === 'undefined') return

        const hasSeenModal = localStorage.getItem(localStorageKey)
        if (!hasSeenModal && showWhenUserHasNotSeenModal) {
            setModalVisibility(true)
        }
    }, [])

    const contextValue: SteppedModalContextType = useMemo(
        () => ({
            isModalVisible,
            setModalVisibility,
            showModal,
            markModalAsSeen,
            steps,
        }),
        [isModalVisible, steps]
    )

    return <SteppedModalContext.Provider value={contextValue}>{children}</SteppedModalContext.Provider>
}
